import React, { useState, useEffect, useCallback } from "react"
import debounce from "lodash.debounce"
import _isEmpty from "lodash/isEmpty"
import { useSelector, useDispatch } from "react-redux"
import {
  getCountryList,
  getstateList,
  selectGenericState,
} from "@/store/features/genericSlice"
import {
  validateField,
  destructureAddress,
  mapStateToCountryHandler,
} from "@/utils/helper"
import { convertToPhoneNumber } from "@/components/RegisterOnlineProduct/v1/RegisterOnlineHelper"
import { getAddressSuggestions } from "@/components/checkout/checkoutHelperFunction"
import { getConfig } from "@/constants/config"
import Input from "@/components/core/Input/Input"
import Dropdown from "@/components/core/SelectDropdown/SelectDropdown"
import Checkbox from "@/components/core/Checkbox/Checkbox"
import emailIcon from "@/public/icons/email.svg"
import phoneIcon from "@/public/icons/phone-icon.svg"

const ContactInfo = props => {
  const { data, setData, i18n, error } = props
  const [countries, setCountries] = useState([])
  const [stateList, setStateList] = useState([])
  const [suggestions, setSuggestions] = useState([])
  const [addressSelected, setAddressSelected] = useState(false)
  const [primaryAddressChecked, setPrimaryAddressChecked] = useState(false)
  const { countryListPath, stateListPath } = useSelector(selectGenericState)
  const dispatch = useDispatch()
  useEffect(() => {
    setData({ ...data, state: {} })
  }, [data.country])

  useEffect(() => {
    try {
      if (!_isEmpty(countryListPath) && !_isEmpty(stateListPath)) getLocations()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error)
    }
  }, [countryListPath, stateListPath])

  useEffect(() => {
    if (data.address && !addressSelected) getSuggestions(data.address)
  }, [data.address])

  const getSuggestions = useCallback(
    debounce(addr => {
      getAddressSuggestions(addr).then(suggData => setSuggestions(suggData))
    }, 300),
    [suggestions]
  )

  const getLocations = async () => {
    if (countryListPath && stateListPath) {
      const countryData = await dispatch(getCountryList())
      const stateData = await dispatch(getstateList())

      const mapList =
        (!_isEmpty(countryData?.payload) &&
          !_isEmpty(stateData?.payload) &&
          mapStateToCountryHandler(countryData?.payload, stateData?.payload)) ||
        []
      setCountries(mapList)
      const config = await getConfig()
      const countryCode = config?.internationalization?.country ?? "US"
      setStateList(mapList.find(c => c?.code === countryCode)?.states ?? [])
      setData({
        ...data,
        country: mapList.find(country => country.code === "US") ?? {},
      })
    }
  }

  const onNameChange = e => {
    const { id, value } = e.target
    const field = id.split("-")
    if (value === "" || validateField("name", value)) {
      setData({
        ...data,
        [field[2]]: value,
      })
    }
  }

  const onInputChange = (e, zipChange = false) => {
    const { id, value } = e.target
    const field = id.split("-")
    if (zipChange) {
      if (value === "" || validateField("zipCodeNumeric", value)) {
        setData({
          ...data,
          [field[2]]: value,
        })
      }
    } else {
      setData({
        ...data,
        [field[2]]: value,
      })
    }
    setAddressSelected(false)
  }

  const onSuggestionSelect = async val => {
    const { general: { siteName } = {} } = await getConfig()
    const addr = destructureAddress(val, siteName)
    setData({
      ...data,
      address: addr.address,
      city: addr.city,
      state: stateList.find(s => s?.code === addr?.state) ?? {},
      zipcode: addr.zipcode,
    })
    props.setError({
      ...error,
      address: false,
      city: false,
      state: false,
      zipcode: false,
    })
    setSuggestions([])
    setAddressSelected(true)
  }

  const changeCountry = country => {
    setStateList(countries?.find(c => c?.name === country)?.states ?? [])
    setData({
      ...data,
      country: countries?.find(c => c?.name === country),
    })
  }

  const changeState = state => {
    setData({ ...data, state: { code: state } ?? {} })
    props.setError({
      ...error,
      state: false,
    })
  }

  return (
    <>
      <fieldset>
        <div className="contact-title">
          <div className="contact-title__fields">
            <legend>
              <h2 className="question">{i18n.contactInformation}</h2>
            </legend>
            <span className="required-fields">{i18n.requiredInfoText}</span>
          </div>
        </div>
        <div className="container contact-info-form">
          <div className="row mx-0">
            <div className={`user-fname col-12 col-sm-6 col-md-6 pl-0 pr-10`}>
              <Input
                id="contact-user-fname"
                type="text"
                value={data.fname}
                maxLength={40}
                placeholder={`${i18n.fname}*`}
                label={`${i18n.fname}*`}
                onChange={onNameChange}
                showError={error.fname}
                errorMessage={error.fname}
                onBlur={() => props.onBlur("fname")}
                autoComplete="given-name"
              />
            </div>
            <div className={`user-lname col-12 col-sm-6 col-md-6 pr-0 pl-10`}>
              <Input
                id="contact-user-lname"
                type="text"
                value={data.lname}
                maxLength={40}
                placeholder={`${i18n.lname}*`}
                label={`${i18n.lname}*`}
                onChange={onNameChange}
                showError={error.lname}
                errorMessage={error.lname}
                onBlur={() => props.onBlur("lname")}
                autoComplete="family-name"
              />
            </div>
          </div>
          <div className="row mx-0">
            <div className="user-email col-12 px-0">
              <Input
                id="contact-user-email"
                type="text"
                value={data.email}
                maxLength={60}
                prefix={<img src={emailIcon?.src} alt="" aria-hidden="true" />}
                placeholder={`${i18n.email}*`}
                label={`${i18n.email}*`}
                onChange={onInputChange}
                showError={error.email}
                errorMessage={error.email}
                onBlur={() => props.onBlur("email")}
                autoComplete="email"
              />
            </div>
          </div>
          <div className="row mx-0">
            <div
              className={`user-address col-12 col-md-12 col-lg-9 pl-0 mobile-pr-0 pr-10`}
            >
              <Input
                id="contact-user-address"
                type="text"
                value={data.address}
                placeholder={`${i18n.address}*`}
                label={`${i18n.address}*`}
                onChange={onInputChange}
                showError={error.address}
                errorMessage={error.address}
                suggestions={suggestions}
                onSelect={onSuggestionSelect}
                onBlur={() => props.onBlur("address")}
                autoComplete="address-line1"
              />
            </div>
            <div
              className={`user-aptFloor col-12 col-md-12 col-lg-3 col-sm-12 pr-0 mobile-pl-0 pl-10`}
            >
              <Input
                id="contact-user-aptFloor"
                type="text"
                value={data.aptFloor}
                autoComplete="address-line2"
                placeholder={i18n.aptFloorSuite}
                label={i18n.aptFloorSuite}
                onChange={onInputChange}
              />
            </div>
          </div>
          <div className="row mx-0">
            <div
              className={`user-city col-12 col-md-12 col-lg-6 pl-0 mobile-pr-0 pr-10`}
            >
              <Input
                id="contact-user-city"
                type="text"
                value={data.city}
                autoComplete={true}
                placeholder={`${i18n.city}*`}
                label={`${i18n.city}*`}
                onChange={onInputChange}
                showError={error.city}
                errorMessage={error.city}
                onBlur={() => props.onBlur("city")}
              />
            </div>
            <div
              className={`user-state col-6 col-md-6 col-lg-3 pr-10 mobile-pl-0 pl-10`}
            >
              <Dropdown
                id="contact-user-state"
                data={stateList ?? []}
                value={data.state.code ?? ""}
                label={`${i18n.state}*`}
                onChange={changeState}
                showError={error.state}
                errorMessage={error.state}
                onBlur={() => props.onBlur("state")}
                ariaStateText={true}
              />
            </div>
            <div className="user-zipcode col-6 col-md-6 col-lg-3 pr-0 pl-10 mobile-pr-0 mobile-pl-10">
              <Input
                id="contact-user-zipcode"
                type="text"
                value={data.zipcode}
                maxLength={7}
                autoComplete="postal-code"
                placeholder={`${i18n.zipcode}*`}
                label={`${i18n.zipcode}*`}
                onChange={e => onInputChange(e, true)}
                showError={error.zipcode}
                errorMessage={error.zipcode}
                onBlur={() => props.onBlur("zipcode")}
              />
            </div>
          </div>
          <div className="row mx-0">
            <div
              className={`user-country col-12 col-sm-6 col-md-6 pl-0 pr-10 mb-20`}
            >
              <Dropdown
                id="contact-user-country"
                data={countries.map(country => country?.name)}
                name="country"
                value={data.country.name ?? ""}
                label={`${i18n.country}`}
                onChange={changeCountry}
                showError={error.country}
                errorMessage={error.country}
              />
            </div>
            <div className={`user-phone col-12 col-sm-6 col-md-6 pr-0 pl-10`}>
              <Input
                id="contact-user-phone"
                type="text"
                maxLength={60}
                placeholder={`${i18n.phoneNumber}*`}
                label={`${i18n.phoneNumber}*`}
                prefix={<img src={phoneIcon?.src} alt="" aria-hidden="true" />}
                value={data.phone}
                onChange={e =>
                  setData({
                    ...data,
                    phone: convertToPhoneNumber(e.target.value),
                  })
                }
                showError={error.phone}
                errorMessage={error.phone}
                onBlur={() => props.onBlur("phone")}
              />
            </div>
          </div>
          <div className="row mx-0">
            <div className="primary-address-checkbox">
              <Checkbox
                value={i18n.primaryAddress}
                checked={primaryAddressChecked}
                onChange={e => {
                  setPrimaryAddressChecked(!primaryAddressChecked)
                  setData({ ...data, isPrimaryAddress: !primaryAddressChecked })
                }}
              />
            </div>
          </div>
          <div className="row mx-0">
            <div className={`col-12 px-0`}>
              <Input
                id="contact-user-label"
                type="text"
                value={data.label}
                maxLength={60}
                placeholder={`${i18n.exHomeLabel}`}
                label={`${i18n.exHomeLabel}`}
                onChange={onInputChange}
              />
            </div>
          </div>
        </div>
      </fieldset>
    </>
  )
}

export default ContactInfo
